import type { TFunction } from "i18next";
import {
  Activity,
  Ban,
  Bell,
  Receipt,
  TrendingDown,
  UserMinus,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { AdminAlertType } from "@/types/admin";

/**
 * Admin alert `type` → label + icon mapping (Sprint 128, TASK-3922).
 *
 * The alerts page and the bell dropdown render each alert's `title` (full,
 * backend-provided sentence) plus a small categorical chip that names the alert
 * *kind* (overdue invoice, low health, churn risk, …). This module is the single
 * source of truth for that chip: it maps each `AdminAlertType` onto a localized
 * label key under `adminPanel.alerts.type.*` and a lucide icon.
 *
 * Sprint-126 analytics added three new alert types (`low_health`, `churn_risk`,
 * `mrr_drop`) which the backend now emits (TASK-3921). They are mapped here so
 * they render with their own label/icon rather than an unlabeled row.
 *
 * Unknown / unmapped types never crash and never render blank: they fall back to
 * a neutral {@link Bell} icon and the raw type string as the label.
 */

/** Resolved chip inputs: a lucide icon plus a display label. */
export interface AlertTypeMeta {
  icon: LucideIcon;
  label: string;
}

const ALERT_TYPE_ICON: Record<AdminAlertType, LucideIcon> = {
  overdue_invoice: Receipt,
  suspended_tenant: Ban,
  optimization_failure: Zap,
  low_health: Activity,
  churn_risk: UserMinus,
  mrr_drop: TrendingDown,
};

/** Neutral fallback icon for an unknown / unmapped alert type. */
const FALLBACK_ICON: LucideIcon = Bell;

/**
 * Resolve `{ icon, label }` for an alert type. Localizes the label via
 * `t("adminPanel.alerts.type.<type>")`, falling back to the raw value so a
 * missing translation still renders the type rather than the key. Unmapped
 * types get the neutral {@link FALLBACK_ICON}.
 */
export function alertTypeMeta(
  type: string | null | undefined,
  t: TFunction,
): AlertTypeMeta {
  const raw = type == null ? "" : String(type).trim();
  const icon = (raw && ALERT_TYPE_ICON[raw as AdminAlertType]) || FALLBACK_ICON;
  return {
    icon,
    label: raw ? t(`adminPanel.alerts.type.${raw}`, { defaultValue: raw }) : "",
  };
}
